import { useEffect, useRef } from 'react';

import { releaseInfo } from '../config/release-info';

const THROTTLE_MS = 15 * 60_000;
const PLACEHOLDER = '__BUILD__';

export type UseReleaseWatcherParams = {
	enabled: boolean;
	onNewRelease: () => void;
};

// Checks the deployed /version.json only when the tab regains focus/visibility,
// throttled to once per THROTTLE_MS, and fires once when its build no longer
// matches the running bundle -- i.e. a newer release is live. Skipped in local
// dev builds where the build stamp is still the unreplaced placeholder.
export const useReleaseWatcher = (params: UseReleaseWatcherParams): void => {
	const { enabled, onNewRelease } = params;
	const firedRef = useRef(false);
	const lastCheckRef = useRef(0);

	useEffect(() => {
		if (!enabled || releaseInfo.build === PLACEHOLDER) return;

		let cancelled = false;

		const check = async (): Promise<void> => {
			try {
				const response = await fetch(`/version.json?ts=${Date.now()}`, { cache: 'no-store' });
				if (!response.ok) return;
				const { build } = await response.json();
				if (cancelled || firedRef.current) return;
				if (typeof build === 'string' && build !== PLACEHOLDER && build !== releaseInfo.build) {
					firedRef.current = true;
					onNewRelease();
				}
			} catch {
				// Best-effort; a transient network failure just retries next check.
			}
		};

		const checkThrottled = (): void => {
			const now = Date.now();
			if (now - lastCheckRef.current < THROTTLE_MS) return;
			lastCheckRef.current = now;
			check();
		};

		const handleVisibility = (): void => {
			if (document.visibilityState === 'visible') checkThrottled();
		};

		checkThrottled();
		window.addEventListener('focus', checkThrottled);
		document.addEventListener('visibilitychange', handleVisibility);

		return () => {
			cancelled = true;
			window.removeEventListener('focus', checkThrottled);
			document.removeEventListener('visibilitychange', handleVisibility);
		};
	}, [enabled, onNewRelease]);
};
